import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SpotifyPanel } from "./SpotifyPanel.js";

import { Avatar } from "../components/Avatar.js";
import { Field } from "../components/Field.js";
import { Action, ErrorLine, Screen } from "../components/Screen.js";
import { HttpError } from "../services/http.js";
import type { MeView } from "../services/http.js";
import {
  clearAvatar,
  currentProfile,
  setPassword,
  updateDisplayName,
  uploadAvatar,
} from "../services/authService.js";

/**
 * Your name, your picture, your password.
 *
 * # Three independent forms, three independent outcomes
 *
 * Each section saves on its own and reports on its own. A single "Save" for the
 * page would mean somebody who changed their name and mistyped a password gets
 * neither — and would have to work out which half failed.
 *
 * # Every write answers with the whole view
 *
 * `PATCH /api/me/profile` and the avatar routes all return the same `MeView` the
 * page loaded with, so a save updates the screen without a second request and
 * without the page inventing what the server now thinks. The avatar in the
 * header changes the instant the upload lands.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const [me, setMe] = useState<MeView | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void currentProfile().then(setMe, () => {
      setFailed(true);
    });
  }, []);

  if (failed) {
    return (
      <Screen title={t("settings.title")}>
        <ErrorLine>{t("settings.failed")}</ErrorLine>
      </Screen>
    );
  }

  // `null` while loading rather than a skeleton of empty fields: an input that
  // renders blank and then fills in is one somebody has already started typing
  // into.
  if (!me) {
    return <Screen title={t("settings.title")}>{null}</Screen>;
  }

  return (
    <Screen title={t("settings.title")} lead={t("settings.lead")}>
      <AvatarSection me={me} onChanged={setMe} />
      <NameSection me={me} onChanged={setMe} />
      <PasswordSection hasPassword={me.has_password} onChanged={setMe} />
      {/* Renders nothing on an installation that does not use Spotify — see
          the panel. Last, because it is the only section that is about
          somebody else's service rather than this account. */}
      <SpotifyPanel />
    </Screen>
  );
}

/** One bordered block per thing you can change. */
function Section({
  title,
  lead,
  children,
}: {
  title: string;
  lead?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-white/10 bg-stage-900/60 p-5 backdrop-blur">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {lead && <p className="mt-1 text-xs leading-relaxed text-white/50">{lead}</p>}
      </div>
      {children}
    </section>
  );
}

function AvatarSection({
  me,
  onChanged,
}: {
  me: MeView;
  onChanged: (me: MeView) => void;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = (task: Promise<MeView>) => {
    setBusy(true);
    setError(null);
    void task.then(
      (next) => {
        onChanged(next);
        setBusy(false);
      },
      (cause: unknown) => {
        // The server's message is the useful one here — it names the actual
        // problem ("too large", "not an image"), which a generic string cannot.
        setError(cause instanceof HttpError ? cause.message : t("settings.failed"));
        setBusy(false);
      },
    );
  };

  return (
    <Section title={t("settings.avatar.title")} lead={t("settings.avatar.lead")}>
      <div className="flex items-center gap-4">
        <Avatar
          name={me.display_name}
          profileId={me.profile_id}
          avatar={me.avatar}
          size={72}
        />
        <div className="flex flex-col gap-2">
          <input
            ref={input}
            type="file"
            // A hint to the picker, not a check — the server sniffs the content,
            // because a filename and a declared type are both caller-supplied.
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared immediately so choosing the *same* file twice still
              // fires a change event — otherwise a failed upload cannot be
              // retried without picking something else first.
              event.target.value = "";
              if (file) send(uploadAvatar(file));
            }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => input.current?.click()}
            className="rounded-lg border border-flash-500/35 bg-flash-500/5 px-4 py-2 text-sm font-medium text-flash-400 transition hover:border-flash-500/60 disabled:opacity-40"
          >
            {busy ? t("settings.avatar.uploading") : t("settings.avatar.choose")}
          </button>
          {me.avatar && (
            <button
              type="button"
              disabled={busy}
              onClick={() => send(clearAvatar())}
              className="text-left text-xs text-white/45 underline underline-offset-4 hover:text-white/70 disabled:opacity-40"
            >
              {t("settings.avatar.remove")}
            </button>
          )}
        </div>
      </div>
      {!me.avatar && (
        <p className="text-xs text-white/40">{t("settings.avatar.generated")}</p>
      )}
      {error && <ErrorLine>{error}</ErrorLine>}
    </Section>
  );
}

function NameSection({ me, onChanged }: { me: MeView; onChanged: (me: MeView) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(me.display_name);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Section title={t("settings.profile.title")}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setSaved(false);
          setError(null);
          void updateDisplayName(name.trim()).then(
            (next) => {
              onChanged(next);
              setSaved(true);
              setBusy(false);
            },
            (cause: unknown) => {
              setError(cause instanceof HttpError ? cause.message : t("settings.failed"));
              setBusy(false);
            },
          );
        }}
      >
        <Field
          id="display-name"
          label={t("settings.profile.label")}
          value={name}
          onChange={(value) => {
            setName(value);
            setSaved(false);
          }}
          autoComplete="nickname"
          maxLength={40}
          required
        />
        {error && <ErrorLine>{error}</ErrorLine>}
        {saved && (
          <p className="text-sm text-correct-500">{t("settings.profile.saved")}</p>
        )}
        <Action disabled={busy || !name.trim() || name.trim() === me.display_name}>
          {t("settings.profile.save")}
        </Action>
      </form>
    </Section>
  );
}

function PasswordSection({
  hasPassword,
  onChanged,
}: {
  hasPassword: boolean;
  onChanged: (me: MeView) => void;
}) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Section
      title={t("settings.password.title")}
      lead={
        hasPassword ? t("settings.password.leadSet") : t("settings.password.leadNone")
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setSaved(false);
          setError(null);
          // `undefined` when there is no password to prove, which is a real
          // state rather than a shortcut: an account that only ever signs in by
          // link has none, and demanding one would lock it out of ever setting
          // a password.
          void setPassword(next, hasPassword ? current : undefined).then(
            () => {
              setSaved(true);
              setBusy(false);
              setCurrent("");
              setNext("");
              // Re-read, because `has_password` has just changed and the lead
              // sentence above is wrong until it does.
              void currentProfile().then(onChanged, () => undefined);
            },
            (cause: unknown) => {
              setError(
                cause instanceof HttpError && cause.code === "invalid_credentials"
                  ? t("settings.password.wrongCurrent")
                  : cause instanceof HttpError
                    ? cause.message
                    : t("settings.failed"),
              );
              setBusy(false);
            },
          );
        }}
      >
        {hasPassword && (
          <Field
            id="current-password"
            label={t("settings.password.current")}
            type="password"
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
            required
          />
        )}
        <Field
          id="new-password"
          label={t("settings.password.new")}
          type="password"
          value={next}
          onChange={setNext}
          autoComplete="new-password"
          required
        />
        {error && <ErrorLine>{error}</ErrorLine>}
        {saved && (
          <p className="text-sm text-correct-500">{t("settings.password.saved")}</p>
        )}
        <Action disabled={busy || !next || (hasPassword && !current)}>
          {hasPassword ? t("settings.password.change") : t("settings.password.set")}
        </Action>
      </form>
    </Section>
  );
}
